import React, { useState, useEffect } from 'react';
import { 
  FileText, CheckCircle, Clock, Users, ExternalLink, ChevronDown, 
  ChevronRight, Github, Video, Globe, Code, Target, Rocket, Search,
  Filter, Download, RotateCcw, AlertTriangle, X, Eye, Info, Layers,
  Hash, Mail, Link, CheckSquare, List, Type, FileQuestion
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get field type icon
const getFieldTypeIcon = (type) => {
  switch (type) {
    case 'text': return <Type size={14} />;
    case 'textarea': return <FileText size={14} />;
    case 'url': return <Link size={14} />;
    case 'email': return <Mail size={14} />;
    case 'number': return <Hash size={14} />;
    case 'checkbox': return <CheckSquare size={14} />;
    case 'select': return <List size={14} />;
    case 'multiselect': return <Layers size={14} />;
    default: return <FileQuestion size={14} />;
  }
};

const AdminTeamSubmissionsSection = ({ token }) => {
  const [submissions, setSubmissions] = useState([]);
  const [byTeam, setByTeam] = useState([]);
  const [stats, setStats] = useState({ total: 0, round1: 0, round2: 0, round3: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [roundFilter, setRoundFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { type: 'problem' | 'round', data: any }
  const [roundFields, setRoundFields] = useState({}); // { "1": [...], "2": [...], "3": [...] }

  useEffect(() => {
    fetchSubmissions();
    fetchRoundFields();
  }, []);

  const fetchRoundFields = async () => {
    try {
      // Fetch custom fields for all rounds
      const fields = {};
      for (const round of [1, 2, 3]) {
        try {
          const response = await axios.get(`${API}/admin/rounds/${round}/fields`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fields[String(round)] = response.data.fields || [];
        } catch (e) {
          fields[String(round)] = [];
        }
      }
      setRoundFields(fields);
    } catch (err) {
      console.error('Failed to fetch round fields:', err);
    }
  };

  const handleResetSubmissions = async () => {
    setResetting(true);
    try {
      if (resetTarget) {
        // Reset single team
        await axios.post(`${API}/admin/team-submissions/${resetTarget.teamId}/reset`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Submissions reset for team ${resetTarget.teamName}`);
      } else {
        // Reset all teams
        await axios.post(`${API}/admin/team-submissions/reset-all`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('All team submissions have been reset');
      }
      setShowResetModal(false);
      setResetTarget(null);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset submissions');
    } finally {
      setResetting(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/team-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data.submissions || []);
      setByTeam(response.data.by_team || []);
      setStats({
        total: response.data.total_count || 0,
        round1: response.data.by_round?.['1'] || 0,
        round2: response.data.by_round?.['2'] || 0,
        round3: response.data.by_round?.['3'] || 0
      });
    } catch (err) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const filteredTeams = byTeam.filter(team => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!team.team_name?.toLowerCase().includes(query) && 
          !team.team_id?.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Round filter
    if (roundFilter !== 'all') {
      if (!team.rounds?.[roundFilter]) {
        return false;
      }
    }
    
    return true;
  });

  const exportToCSV = () => {
    const headers = [
      'Team ID', 'Team Name', 'Problem Statement', 'Theme',
      'Round 1 Status', 'Round 1 Submitted At',
      'Round 2 Status', 'Round 2 GitHub', 'Round 2 Submitted At',
      'Round 3 Status', 'Round 3 GitHub', 'Round 3 Submitted At'
    ];
    
    const rows = byTeam.map(team => [
      team.team_id,
      team.team_name,
      team.problem_statement?.title || 'Not Selected',
      team.problem_statement?.theme || 'N/A',
      team.rounds?.['1'] ? 'Submitted' : 'Pending',
      team.rounds?.['1']?.submitted_at || '',
      team.rounds?.['2'] ? 'Submitted' : 'Pending',
      team.rounds?.['2']?.github_url || '',
      team.rounds?.['2']?.submitted_at || '',
      team.rounds?.['3'] ? 'Submitted' : 'Pending',
      team.rounds?.['3']?.github_url || '',
      team.rounds?.['3']?.submitted_at || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `team_submissions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Submissions exported to CSV');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-submissions-section">
      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {resetTarget ? 'Reset Team Submissions?' : 'Reset ALL Submissions?'}
              </h3>
              <p className="text-gray-600 mb-4">
                {resetTarget 
                  ? `This will delete all submissions (Round 1, 2, 3) and clear the problem statement selection for team "${resetTarget.teamName}".`
                  : 'This will delete ALL submissions for ALL teams and clear ALL problem statement selections. This action cannot be undone!'
                }
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowResetModal(false); setResetTarget(null); }}
                  className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                  disabled={resetting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetSubmissions}
                  disabled={resetting}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="confirm-reset-btn"
                >
                  {resetting ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <RotateCcw size={18} />
                      Reset
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Problem Statement Detail Modal */}
      {detailModal?.type === 'problem' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Problem Statement</h3>
                  <p className="text-sm text-gray-500">Selected by {detailModal.teamName}</p>
                </div>
              </div>
              <button
                onClick={() => setDetailModal(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="close-problem-modal"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{detailModal.data.title}</h4>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      {detailModal.data.theme}
                    </span>
                    {detailModal.data.difficulty && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        detailModal.data.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                        detailModal.data.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {detailModal.data.difficulty.charAt(0).toUpperCase() + detailModal.data.difficulty.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Info size={16} /> Description
                  </h5>
                  <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {detailModal.data.description || 'No description provided'}
                  </p>
                </div>

                {detailModal.data.scope && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Layers size={16} /> Scope
                    </h5>
                    <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {detailModal.data.scope}
                    </p>
                  </div>
                )}

                {detailModal.data.requirements && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <CheckSquare size={16} /> Requirements
                    </h5>
                    <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {detailModal.data.requirements}
                    </p>
                  </div>
                )}

                {detailModal.data.resources && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Link size={16} /> Resources
                    </h5>
                    <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {detailModal.data.resources}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Round Submission Detail Modal */}
      {detailModal?.type === 'round' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className={`flex items-center justify-between p-6 border-b border-gray-200 ${
              detailModal.roundNum === 1 ? 'bg-gradient-to-r from-green-50 to-emerald-50' :
              detailModal.roundNum === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50' :
              'bg-gradient-to-r from-purple-50 to-violet-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  detailModal.roundNum === 1 ? 'bg-green-100' :
                  detailModal.roundNum === 2 ? 'bg-orange-100' :
                  'bg-purple-100'
                }`}>
                  {detailModal.roundNum === 1 ? <Clock className="text-green-600" size={24} /> :
                   detailModal.roundNum === 2 ? <Code className="text-orange-600" size={24} /> :
                   <Rocket className="text-purple-600" size={24} />}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Round {detailModal.roundNum} Submission</h3>
                  <p className="text-sm text-gray-500">{detailModal.teamName}</p>
                </div>
              </div>
              <button
                onClick={() => setDetailModal(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="close-round-modal"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="space-y-6">
                {/* Submission Timestamp */}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock size={16} />
                  <span>Submitted: {new Date(detailModal.data.submitted_at).toLocaleString()}</span>
                </div>

                {/* Default Fields */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Round 1 specific fields */}
                  {detailModal.roundNum === 1 && (
                    <>
                      {detailModal.data.business_scope && (
                        <div className="md:col-span-2">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Business Scope</h5>
                          <p className="text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                            {detailModal.data.business_scope}
                          </p>
                        </div>
                      )}
                      {detailModal.data.social_impact && (
                        <div className="md:col-span-2">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Social Impact</h5>
                          <p className="text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                            {detailModal.data.social_impact}
                          </p>
                        </div>
                      )}
                      {detailModal.data.tech_stack && detailModal.data.tech_stack.length > 0 && (
                        <div className="md:col-span-2">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Tech Stack</h5>
                          <div className="flex flex-wrap gap-2">
                            {detailModal.data.tech_stack.map((tech, idx) => (
                              <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {detailModal.data.member_expertise && (
                        <div className="md:col-span-2">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">Member Expertise</h5>
                          <p className="text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 whitespace-pre-wrap">
                            {detailModal.data.member_expertise}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Round 2 & 3 links */}
                  {(detailModal.roundNum === 2 || detailModal.roundNum === 3) && (
                    <>
                      {detailModal.data.github_url && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <Github size={16} /> GitHub Repository
                          </h5>
                          <a
                            href={detailModal.data.github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-200"
                          >
                            {detailModal.data.github_url}
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                      {detailModal.data.demo_url && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <Globe size={16} /> Demo URL
                          </h5>
                          <a
                            href={detailModal.data.demo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-green-600 hover:text-green-800 bg-green-50 p-3 rounded-lg border border-green-200"
                          >
                            {detailModal.data.demo_url}
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                      {detailModal.data.video_url && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <Video size={16} /> Video Presentation
                          </h5>
                          <a
                            href={detailModal.data.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-red-600 hover:text-red-800 bg-red-50 p-3 rounded-lg border border-red-200"
                          >
                            {detailModal.data.video_url}
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Custom Fields (Dynamic) */}
                {(detailModal.data.data && Object.keys(detailModal.data.data).length > 0) || 
                 (detailModal.data.fields_data && Object.keys(detailModal.data.fields_data).length > 0) ? (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Layers size={18} /> Custom Fields
                    </h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      {Object.entries(detailModal.data.fields_data || detailModal.data.data || {}).map(([fieldName, value]) => {
                        // Try to find the field label from roundFields
                        const fieldDef = roundFields[String(detailModal.roundNum)]?.find(f => f.field_name === fieldName);
                        const displayLabel = fieldDef?.field_label || fieldName.replace(/_[a-f0-9]+$/i, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        const fieldType = fieldDef?.field_type || (
                          typeof value === 'boolean' ? 'checkbox' :
                          Array.isArray(value) ? 'multiselect' :
                          String(value).startsWith('http') ? 'url' :
                          String(value).includes('@') ? 'email' :
                          'text'
                        );
                        
                        // Skip default fields that are already displayed above
                        const defaultFields = ['business_scope', 'social_impact', 'tech_stack', 'member_expertise', 'github_url', 'demo_url', 'video_url', 'problem_statement', 'team_id', 'team_name', 'round_number', 'submitted_at'];
                        if (defaultFields.includes(fieldName)) return null;

                        return (
                          <div key={fieldName} className={fieldType === 'textarea' || String(value).length > 100 ? 'md:col-span-2' : ''}>
                            <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              {getFieldTypeIcon(fieldType)}
                              {displayLabel}
                            </h5>
                            {fieldType === 'url' && value ? (
                              <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-200 break-all"
                              >
                                {value}
                                <ExternalLink size={14} className="flex-shrink-0" />
                              </a>
                            ) : fieldType === 'checkbox' || typeof value === 'boolean' ? (
                              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                                value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {value ? <CheckCircle size={14} /> : <X size={14} />}
                                {value ? 'Yes' : 'No'}
                              </span>
                            ) : Array.isArray(value) ? (
                              <div className="flex flex-wrap gap-2">
                                {value.map((v, idx) => (
                                  <span key={idx} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                                    {v}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 whitespace-pre-wrap">
                                {value || <span className="text-gray-400 italic">Not provided</span>}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border-2 border-blue-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Submissions</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-green-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Clock className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Round 1</p>
              <p className="text-2xl font-bold text-green-600">{stats.round1}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-orange-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Code className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Round 2</p>
              <p className="text-2xl font-bold text-orange-600">{stats.round2}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-purple-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Rocket className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Round 3 (Final)</p>
              <p className="text-2xl font-bold text-purple-600">{stats.round3}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by team name or ID..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-submissions"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={roundFilter}
              onChange={(e) => setRoundFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="round-filter"
            >
              <option value="all">All Rounds</option>
              <option value="1">Round 1 Only</option>
              <option value="2">Round 2 Only</option>
              <option value="3">Round 3 Only</option>
            </select>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              data-testid="export-submissions-btn"
            >
              <Download size={18} />
              Export CSV
            </button>
            <button
              onClick={() => { setResetTarget(null); setShowResetModal(true); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              data-testid="reset-all-btn"
            >
              <RotateCcw size={18} />
              Reset All
            </button>
          </div>
        </div>
      </div>

      {/* Teams List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-900">Team Submissions ({filteredTeams.length})</h3>
        </div>
        
        {filteredTeams.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No submissions found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredTeams.map((team) => (
              <div key={team.team_id} className="hover:bg-gray-50">
                {/* Team Header */}
                <button
                  onClick={() => toggleTeam(team.team_id)}
                  className="w-full px-6 py-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {expandedTeams[team.team_id] ? 
                        <ChevronDown size={20} className="text-gray-400" /> : 
                        <ChevronRight size={20} className="text-gray-400" />
                      }
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900">{team.team_name}</h4>
                        <p className="text-xs text-gray-500">{team.team_id}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Round Status Badges */}
                    {[1, 2, 3].map(round => (
                      <span
                        key={round}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          team.rounds?.[String(round)]
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        R{round}: {team.rounds?.[String(round)] ? '✓' : '○'}
                      </span>
                    ))}
                  </div>
                </button>
                
                {/* Expanded Details */}
                {expandedTeams[team.team_id] && (
                  <div className="px-6 pb-4 space-y-4 bg-gray-50">
                    {/* Actions Bar */}
                    <div className="flex justify-end">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setResetTarget({ teamId: team.team_id, teamName: team.team_name });
                          setShowResetModal(true);
                        }}
                        className="px-3 py-1.5 text-xs font-medium bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1 transition-colors"
                        data-testid={`reset-team-${team.team_id}`}
                      >
                        <RotateCcw size={14} />
                        Reset This Team's Submissions
                      </button>
                    </div>

                    {/* Problem Statement */}
                    {team.problem_statement && (
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Target className="text-blue-600" size={16} />
                            <span className="text-xs font-medium text-gray-500 uppercase">Problem Statement</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailModal({
                                type: 'problem',
                                data: team.problem_statement,
                                teamName: team.team_name
                              });
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                            data-testid={`view-problem-${team.team_id}`}
                          >
                            <Eye size={12} />
                            View Full Details
                          </button>
                        </div>
                        <h5 className="font-semibold text-gray-900">{team.problem_statement.title}</h5>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{team.problem_statement.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                            {team.problem_statement.theme}
                          </span>
                          {team.problem_statement.difficulty && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              team.problem_statement.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                              team.problem_statement.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {team.problem_statement.difficulty.charAt(0).toUpperCase() + team.problem_statement.difficulty.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* No Problem Statement Selected */}
                    {!team.problem_statement && (
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="text-amber-600" size={16} />
                          <span className="text-sm font-medium text-amber-700">No problem statement selected yet</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Round Submissions */}
                    <div className="grid md:grid-cols-3 gap-4">
                      {[1, 2, 3].map(round => {
                        const submission = team.rounds?.[String(round)];
                        const fieldsData = submission?.fields_data || submission?.data || {};
                        const customFieldsCount = Object.keys(fieldsData).filter(k => 
                          !['business_scope', 'social_impact', 'tech_stack', 'member_expertise', 'github_url', 'demo_url', 'video_url', 'problem_statement', 'team_id', 'team_name', 'round_number', 'submitted_at'].includes(k)
                        ).length;
                        
                        return (
                          <div
                            key={round}
                            className={`p-4 rounded-lg border-2 ${
                              submission ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                            }`}
                            data-testid={`round-${round}-card-${team.team_id}`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-semibold text-gray-800">
                                Round {round}
                              </h5>
                              {submission ? (
                                <CheckCircle className="text-green-600" size={18} />
                              ) : (
                                <Clock className="text-gray-400" size={18} />
                              )}
                            </div>
                            
                            {submission ? (
                              <div className="space-y-2 text-sm">
                                {round === 1 ? (
                                  <>
                                    <div>
                                      <span className="text-gray-500">Tech Stack:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {submission.tech_stack?.slice(0, 3).map(tech => (
                                          <span key={tech} className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                                            {tech}
                                          </span>
                                        ))}
                                        {submission.tech_stack?.length > 3 && (
                                          <span className="text-xs text-gray-500">+{submission.tech_stack.length - 3}</span>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {submission.github_url && (
                                      <a
                                        href={submission.github_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Github size={14} /> GitHub <ExternalLink size={12} />
                                      </a>
                                    )}
                                    {submission.demo_url && (
                                      <a
                                        href={submission.demo_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-green-600 hover:text-green-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Globe size={14} /> Demo <ExternalLink size={12} />
                                      </a>
                                    )}
                                    {submission.video_url && (
                                      <a
                                        href={submission.video_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-red-600 hover:text-red-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Video size={14} /> Video <ExternalLink size={12} />
                                      </a>
                                    )}
                                  </>
                                )}
                                
                                {/* Custom fields indicator */}
                                {customFieldsCount > 0 && (
                                  <div className="flex items-center gap-1 text-xs text-indigo-600">
                                    <Layers size={12} />
                                    <span>{customFieldsCount} custom field{customFieldsCount > 1 ? 's' : ''}</span>
                                  </div>
                                )}
                                
                                <p className="text-xs text-gray-400">
                                  {new Date(submission.submitted_at).toLocaleString()}
                                </p>
                                
                                {/* View Details Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDetailModal({
                                      type: 'round',
                                      roundNum: round,
                                      data: submission,
                                      teamName: team.team_name
                                    });
                                  }}
                                  className="w-full mt-2 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-100 text-gray-700 rounded-lg border border-gray-300 transition-colors"
                                  data-testid={`view-round-${round}-${team.team_id}`}
                                >
                                  <Eye size={12} />
                                  View Full Details
                                </button>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400">Not submitted</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTeamSubmissionsSection;
